import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Search, Book, CheckCircle, ArrowRight } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "The Great Archive",
        icon: "📚",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">What is a Database?</h3>
                    <p className="text-slate-700 mb-4">
                        Imagine a library with a billion books, but no shelves. How would you find anything?
                    </p>
                    <p className="text-slate-700">
                        A <strong>Database</strong> is an organized collection of data.
                        We look things up using a special language called <strong>SQL</strong> (Structured Query Language).
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Spell: SELECT",
        icon: "🪄",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 mb-4">A SQL query asks the database a question:</p>
                <div className="bg-slate-900 text-green-400 p-6 rounded-xl font-mono text-sm shadow-xl">
                    <div><span className="text-purple-400">SELECT</span> title, author</div>
                    <div><span className="text-purple-400">FROM</span> Books</div>
                    <div><span className="text-purple-400">WHERE</span> year {'>'} 2000;</div>
                </div>
                <ul className="space-y-2 text-sm mt-4">
                    <li className="flex gap-2">
                        <span className="bg-purple-100 text-purple-700 font-bold px-2 rounded">SELECT</span>
                        <span className="text-slate-600">Which columns do you want?</span>
                    </li>
                    <li className="flex gap-2">
                        <span className="bg-purple-100 text-purple-700 font-bold px-2 rounded">FROM</span>
                        <span className="text-slate-600">Which table (list) holds the data?</span>
                    </li>
                    <li className="flex gap-2">
                        <span className="bg-purple-100 text-purple-700 font-bold px-2 rounded">WHERE</span>
                        <span className="text-slate-600">Filter the results (e.g. only new titles).</span>
                    </li>
                </ul>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🕵️",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">Find the Lost Tome</h3>
                    <p className="text-slate-700 mb-4">
                        A very special book is hidden in the archive.
                    </p>
                    <p className="font-bold text-purple-900">
                        Mission: Use the query builder to find the book "Code for Monkeys".
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
const BOOKS = [
    { id: 1, title: "The Jungle Book", genre: "Adventure", year: 1894, color: "bg-green-500" },
    { id: 2, title: "Gorillas in Mist", genre: "Nature", year: 1983, color: "bg-blue-500" },
    { id: 3, title: "Banana Recipes", genre: "Cooking", year: 2020, color: "bg-yellow-500" },
    { id: 4, title: "Code for Monkeys", genre: "Tech", year: 2025, color: "bg-purple-500" },
    { id: 5, title: "Tarzan", genre: "Adventure", year: 1912, color: "bg-orange-500" },
    { id: 6, title: "SQL for Dummies", genre: "Tech", year: 2010, color: "bg-red-500" },
    { id: 7, title: "King Kong", genre: "Adventure", year: 1933, color: "bg-slate-700" },
    { id: 8, title: "Vegan Diet", genre: "Cooking", year: 2022, color: "bg-lime-500" },
];

const LibraryGame = ({ onComplete }: { onComplete: () => void }) => {
    const [filterGenre, setFilterGenre] = useState<string | null>(null);
    const [filterYear, setFilterYear] = useState<number | null>(null);
    const [found, setFound] = useState(false);

    // Simulate SQL Query Construction
    const whereClause = [];
    if (filterGenre) whereClause.push(`Genre = '${filterGenre}'`);
    if (filterYear) whereClause.push(`Year > ${filterYear}`);

    // Filter Logic
    const filteredBooks = BOOKS.filter(book => {
        if (filterGenre && book.genre !== filterGenre) return false;
        if (filterYear && book.year <= filterYear) return false;
        return true;
    });

    const checkOutBook = (book: typeof BOOKS[0]) => {
        if (book.title === "Code for Monkeys") {
            setFound(true);
            onComplete();
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* LEFT: Librarian Interface (Query Builder) */}
            <div className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-xl border-4 border-amber-900/10 relative z-10">
                <div className="flex justify-center -mt-12 mb-4">
                    <div className="text-6xl">🦍</div>
                </div>

                <div className="space-y-6">
                    <div className="bg-slate-900 p-4 rounded-xl border-2 border-slate-700 shadow-inner">
                        <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Current Query:</label>
                        <code className="text-green-400 font-mono text-sm break-all">
                            <span className="text-purple-400">SELECT</span> * <span className="text-purple-400">FROM</span> Books
                            {whereClause.length > 0 && <br />}
                            {whereClause.length > 0 && <span className="text-purple-400"> WHERE </span>}
                            {whereClause.join(' AND ')}
                            ;
                        </code>
                    </div>

                    <div>
                        <label className="font-bold text-amber-900 mb-2 block">Filter by Genre:</label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilterGenre(null)}
                                className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${!filterGenre ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-400 border-slate-200'}`}
                            >
                                All
                            </button>
                            {Array.from(new Set(BOOKS.map(b => b.genre))).map(g => (
                                <button
                                    key={g}
                                    onClick={() => setFilterGenre(g === filterGenre ? null : g)}
                                    className={`px-3 py-1 rounded-full text-sm font-bold border-2 transition-colors ${filterGenre === g ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-400 border-slate-200 hover:border-amber-300'}`}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="font-bold text-amber-900 mb-2 block">Published After Year:</label>
                        <input
                            type="range"
                            min="1800" max="2025" step="10"
                            value={filterYear || 1800}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setFilterYear(val === 1800 ? null : val);
                            }}
                            className="w-full accent-amber-600"
                        />
                        <div className="text-right font-mono font-bold text-amber-600">
                            {filterYear ? `> ${filterYear}` : 'Any Year'}
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT: The Bookshelf */}
            <div className="lg:col-span-2 bg-[#5c4033] p-8 rounded-3xl shadow-2xl border-8 border-[#3e2b22] relative min-h-[500px]">
                {/* Shelves */}
                <div className="absolute top-1/3 left-4 right-4 h-4 bg-[#3e2b22] shadow-xl rounded"></div>
                <div className="absolute top-2/3 left-4 right-4 h-4 bg-[#3e2b22] shadow-xl rounded"></div>

                <div className="relative z-10 flex flex-wrap gap-x-4 gap-y-16 justify-start items-end place-content-start h-full pb-8">
                    <AnimatePresence>
                        {filteredBooks.map((book) => (
                            <motion.div
                                layout
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                key={book.id}
                                className={`${book.color} w-24 h-32 rounded-r-lg rounded-l-sm border-l-4 border-white/20 shadow-lg flex flex-col justify-between p-2 cursor-pointer hover:-translate-y-2 transition-transform`}
                            >
                                <div className="text-[10px] text-white/80 font-bold uppercase truncate">{book.genre}</div>
                                <div className="text-white font-serif font-bold text-sm leading-tight line-clamp-3 text-center">
                                    {book.title}
                                </div>
                                <div className="text-[10px] text-white/60 font-mono text-center">{book.year}</div>
                                {book.title === "Code for Monkeys" && !found && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); checkOutBook(book); }}
                                        className="mt-2 text-[10px] bg-white text-purple-600 font-bold px-2 py-1 rounded-full shadow hover:bg-purple-100 w-full"
                                    >
                                        Found It!
                                    </button>
                                )}
                                {book.title === "Code for Monkeys" && found && (
                                    <div className="mt-2 text-[10px] bg-green-500 text-white font-bold px-2 py-1 rounded-full shadow w-full flex justify-center items-center gap-1">
                                        <CheckCircle size={10} /> Got it!
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {filteredBooks.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center text-white/50 font-serif italic text-xl">
                            No books found in the archives...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const LibrarySQL = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('data-detective-sql', 50, 'badge_data-detective');
    };

    return (
        <LessonWrapper
            moduleName="Library of SQL"
            moduleEmoji="🏰"
            moduleColor="amber"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Search Archive"
        >
            <LibraryGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default LibrarySQL;
